import { useState, useEffect, useCallback, useMemo } from 'react';

const API = '';
const token = () => localStorage.getItem('token');

const PLAN_COLORS = {
  free: { bg: '#2a2a2a', color: '#888', label: 'Free' },
  premium: { bg: '#1565c0', color: '#90caf9', label: 'Premium' },
  ultimate: { bg: '#6a1b9a', color: '#ce93d8', label: 'Ultimate' },
  admin: { bg: '#b71c1c', color: '#ffcdd2', label: 'Admin' },
  mirror: { bg: '#00695c', color: '#80cbc4', label: 'Mirror' },
};

function PlanBadge({ plan }) {
  const color = PLAN_COLORS[plan] || PLAN_COLORS.free;
  return <span style={{ background: color.bg, color: color.color, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 'bold' }}>{color.label}</span>;
}

function RoleBadge({ user }) {
  if (user.user_role === 'delivery_only') {
    return <span style={{ background: '#4c1d95', color: '#ddd6fe', padding: '3px 9px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>Delivery Only</span>;
  }
  return <span style={{ background: '#263238', color: '#b0bec5', padding: '3px 9px', borderRadius: 12, fontSize: 11 }}>Standard</span>;
}

function CityCheckboxes({ cities, selected, disabled, onToggle }) {
  if (!cities.length) return <span style={{ color: '#777', fontSize: 11 }}>Noch keine Orte angelegt</span>;
  return (
    <div style={styles.cityGrid}>
      {cities.map((city) => {
        const checked = selected.includes(city);
        return (
          <label key={city} style={{ ...styles.cityOption, opacity: disabled ? 0.55 : 1 }}>
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={() => onToggle(city, !checked)}
            />
            <span>{city}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [cities, setCities] = useState([]);
  const [importAccounts, setImportAccounts] = useState([]);
  const [importDrafts, setImportDrafts] = useState({});
  const [savingImportAccount, setSavingImportAccount] = useState(null);
  const [creatingImportAccount, setCreatingImportAccount] = useState(false);
  const [importCreateError, setImportCreateError] = useState('');
  const [newImportAccount, setNewImportAccount] = useState({ userId: '', email: '', displayName: '' });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(null);
  const [savingCities, setSavingCities] = useState(null);
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [addingCity, setAddingCity] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', password: '', ownerUserId: '', allowedCities: [] });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [usersResponse, statsResponse, citiesResponse, importAccountsResponse] = await Promise.all([
        fetch(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token()}` } }),
        fetch(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token()}` } }),
        fetch(`${API}/admin/delivery-cities`, { headers: { Authorization: `Bearer ${token()}` } }),
        fetch(`${API}/admin/import-accounts`, { headers: { Authorization: `Bearer ${token()}` } }),
      ]);
      if (usersResponse.status === 403) {
        setError('Kein Admin-Zugriff');
        setLoading(false);
        return;
      }
      const usersData = await usersResponse.json();
      const statsData = await statsResponse.json();
      const citiesData = await citiesResponse.json();
      const importAccountsData = importAccountsResponse.ok ? await importAccountsResponse.json() : { accounts: [] };
      setUsers(usersData.users || []);
      setStats(statsData);
      setCities(citiesData.cities || []);
      const loadedImportAccounts = importAccountsData.accounts || [];
      setImportAccounts(loadedImportAccounts);
      setImportDrafts(Object.fromEntries(loadedImportAccounts.map((account) => [String(account.id), {
        accountName: account.account_name || '',
        displayName: account.display_name || '',
        email: account.email || '',
      }])));
      setCreateForm((current) => ({
        ...current,
        ownerUserId: current.ownerUserId || String((usersData.users || []).find((user) => user.plan === 'admin' && user.user_role !== 'delivery_only')?.id || ''),
      }));
    } catch {
      setError('Verbindungsfehler');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function changePlan(userId, plan) {
    setSaving(userId);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/plan`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ plan }),
      });
      const data = await response.json();
      if (data.ok) setUsers((current) => current.map((user) => user.id === userId ? { ...user, plan } : user));
      else alert(data.error);
    } catch {
      alert('Fehler beim Speichern');
    }
    setSaving(null);
  }

  async function addCity(event) {
    event.preventDefault();
    const city = cityInput.trim();
    if (!city) return;
    setAddingCity(true);
    try {
      const response = await fetch(`${API}/admin/delivery-cities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ city }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Ort konnte nicht angelegt werden');
      setCities((current) => [...new Set([...current, data.city])].sort((a, b) => a.localeCompare(b, 'de')));
      setCityInput('');
    } catch (err) {
      alert(err.message);
    } finally {
      setAddingCity(false);
    }
  }

  async function createDeliveryUser(event) {
    event.preventDefault();
    setCreating(true);
    setCreateError('');
    try {
      const response = await fetch(`${API}/admin/delivery-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          email: createForm.email,
          password: createForm.password,
          ownerUserId: Number(createForm.ownerUserId),
          allowedCities: createForm.allowedCities,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Benutzer konnte nicht erstellt werden');
      setCreateForm((current) => ({ ...current, email: '', password: '', allowedCities: [] }));
      await fetchData();
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  }

  async function saveUserCities(userId, nextCities) {
    setSavingCities(userId);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/delivery-cities`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ cities: nextCities }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Ortsfreigaben konnten nicht gespeichert werden');
      setUsers((current) => current.map((user) => Number(user.id) === Number(userId)
        ? { ...user, delivery_allowed_cities: data.user.delivery_allowed_cities || nextCities }
        : user));
    } catch (err) {
      alert(err.message);
      await fetchData();
    } finally {
      setSavingCities(null);
    }
  }

  function toggleUserCity(user, city, checked) {
    const stored = user.delivery_allowed_cities || [];
    const isAdminTarget = user.plan === 'admin';
    const current = isAdminTarget && stored.length === 0 ? [...cities] : stored;
    const next = checked
      ? [...new Set([...current, city])]
      : current.filter((item) => item !== city);

    // Beim Admin steht [] für uneingeschränkten Zugriff auf alle aktuellen
    // und künftig neu erkannten Orte. Eine Teilmenge schränkt ihn verbindlich ein.
    const persisted = isAdminTarget && next.length === cities.length ? [] : next;
    setUsers((items) => items.map((item) => Number(item.id) === Number(user.id)
      ? { ...item, delivery_allowed_cities: persisted }
      : item));
    saveUserCities(user.id, persisted);
  }

  async function createImportAccount(event) {
    event.preventDefault();
    const userId = Number(newImportAccount.userId);
    const email = String(newImportAccount.email || '').trim();
    const displayName = String(newImportAccount.displayName || '').trim();
    if (!Number.isInteger(userId) || userId <= 0 || !email) return;

    setCreatingImportAccount(true);
    setImportCreateError('');
    try {
      const response = await fetch(`${API}/admin/import-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ userId, email, displayName }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Import-Adresse konnte nicht angelegt werden');
      setImportAccounts((current) => [...current, data.account].sort((a, b) => {
        const userCompare = String(a.user_email || '').localeCompare(String(b.user_email || ''), 'de');
        if (userCompare !== 0) return userCompare;
        return (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || Number(a.id) - Number(b.id);
      }));
      setNewImportAccount((current) => ({ ...current, email: '', displayName: '' }));
    } catch (err) {
      setImportCreateError(err.message);
    } finally {
      setCreatingImportAccount(false);
    }
  }

  function updateImportDraft(account, field, value) {
    setImportDrafts((current) => ({
      ...current,
      [String(account.id)]: {
        accountName: current[String(account.id)]?.accountName ?? account.account_name ?? '',
        displayName: current[String(account.id)]?.displayName ?? account.display_name ?? '',
        email: current[String(account.id)]?.email ?? account.email ?? '',
        [field]: value,
      },
    }));
  }

  async function saveImportAccount(account) {
    const draft = importDrafts[String(account.id)] || {
      accountName: account.account_name || '',
      displayName: account.display_name || '',
      email: account.email || '',
    };
    const accountName = String(draft.accountName || '').trim();
    if (!accountName) {
      alert('Account-Name darf nicht leer sein');
      return;
    }

    setSavingImportAccount(account.id);
    try {
      const response = await fetch(`${API}/admin/import-accounts/${account.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          accountName,
          displayName: String(draft.displayName || '').trim(),
          email: String(draft.email || '').trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Import-Account konnte nicht gespeichert werden');

      setImportAccounts((current) => current.map((item) => Number(item.id) === Number(account.id)
        ? { ...item, ...data.account }
        : item));
      setImportDrafts((current) => ({
        ...current,
        [String(account.id)]: {
          accountName: data.account.account_name || '',
          displayName: data.account.display_name || '',
          email: data.account.email || '',
        },
      }));
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingImportAccount(null);
    }
  }

  async function deleteUser(userId) {
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await response.json();
      if (data.ok) setUsers((current) => current.filter((user) => user.id !== userId));
      else alert(data.error);
    } catch {
      alert('Fehler beim Löschen');
    }
    setConfirm(null);
  }

  const owners = useMemo(() => users.filter((user) => user.user_role !== 'delivery_only'), [users]);
  const importOwners = useMemo(
    () => users.filter((user) => user.user_role !== 'delivery_only' && user.plan !== 'mirror'),
    [users]
  );
  const filtered = users.filter((user) =>
    user.email.toLowerCase().includes(search.toLowerCase()) ||
    String(user.delivery_operator_code || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div style={styles.page}><div style={styles.loading}>⏳ Lädt...</div></div>;
  if (error) return <div style={styles.page}><div style={styles.error}>❌ {error}</div></div>;

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>⚙️ Admin Panel</h1>

      {stats && (
        <div style={styles.statsRow}>
          <StatCard label="Gesamt User" value={stats.users.total_users} color="#555" />
          <StatCard label="Free" value={stats.users.free_users} color="#555" />
          <StatCard label="Premium" value={stats.users.premium_users} color="#1565c0" />
          <StatCard label="Ultimate" value={stats.users.ultimate_users} color="#6a1b9a" />
          <StatCard label="Admin" value={stats.users.admin_users} color="#b71c1c" />
          <StatCard label="Delivery" value={stats.users.delivery_users} color="#7c3aed" />
          <StatCard label="Bestellungen" value={stats.orders} color="#2e7d32" />
          <StatCard label="Wishlist" value={stats.wishlist} color="#e65100" />
        </div>
      )}

      <form onSubmit={addCity} style={styles.cityManager}>
        <div>
          <div style={styles.createTitle}>Lieferorte verwalten</div>
          <div style={styles.createHint}>Erkannte Orte erscheinen automatisch. Weitere Orte können hier ergänzt werden.</div>
        </div>
        <input style={styles.formInput} value={cityInput} onChange={(event) => setCityInput(event.target.value)} placeholder="Ort hinzufügen, z. B. Bad Wimpfen" />
        <button style={styles.secondaryBtn} disabled={addingCity} type="submit">{addingCity ? 'Wird angelegt...' : 'Ort hinzufügen'}</button>
      </form>

      <form onSubmit={createDeliveryUser} style={styles.createCard}>
        <div style={{ flex: '1 1 220px' }}>
          <div style={styles.createTitle}>Delivery-Benutzer anlegen</div>
          <div style={styles.createHint}>Dieser Benutzer sieht ausschließlich „Delivery Status“ und nur die markierten Orte.</div>
        </div>
        <input style={styles.formInput} type="email" placeholder="Email" value={createForm.email} onChange={(event) => setCreateForm((current) => ({ ...current, email: event.target.value }))} required />
        <input style={styles.formInput} type="password" minLength={8} placeholder="Startpasswort (min. 8 Zeichen)" value={createForm.password} onChange={(event) => setCreateForm((current) => ({ ...current, password: event.target.value }))} required />
        <select style={styles.formInput} value={createForm.ownerUserId} onChange={(event) => setCreateForm((current) => ({ ...current, ownerUserId: event.target.value }))} required>
          <option value="">Daten-Eigentümer wählen</option>
          {owners.map((owner) => <option key={owner.id} value={owner.id}>#{owner.id} · {owner.email}</option>)}
        </select>
        <button style={styles.createBtn} disabled={creating} type="submit">{creating ? 'Wird angelegt...' : 'Delivery-Benutzer anlegen'}</button>
        <div style={{ width: '100%' }}>
          <div style={styles.cityLabel}>Sichtbare Orte</div>
          <CityCheckboxes
            cities={cities}
            selected={createForm.allowedCities}
            disabled={creating}
            onToggle={(city, checked) => setCreateForm((current) => ({
              ...current,
              allowedCities: checked
                ? [...new Set([...current.allowedCities, city])]
                : current.allowedCities.filter((item) => item !== city),
            }))}
          />
        </div>
        {createError && <div style={styles.createError}>{createError}</div>}
      </form>

      <div style={styles.importCard}>
        <div style={{ marginBottom: 12 }}>
          <div style={styles.importTitle}>📨 Mail-Import-Zuordnung</div>
          <div style={styles.importHint}>
            Jede ursprüngliche Amazon-E-Mail wird genau einem Benutzer und Account zugeordnet. Dadurch landen Philips Mails ausschließlich bei Philip und deine ausschließlich bei dir.
          </div>
        </div>

        <form onSubmit={createImportAccount} style={styles.importCreateRow}>
          <select
            style={styles.importCreateSelect}
            value={newImportAccount.userId}
            onChange={(event) => setNewImportAccount((current) => ({ ...current, userId: event.target.value }))}
            disabled={creatingImportAccount}
            required
          >
            <option value="">Benutzer auswählen</option>
            {importOwners.map((user) => (
              <option key={user.id} value={user.id}>#{user.id} · {user.email}</option>
            ))}
          </select>
          <input
            style={styles.importCreateInput}
            type="email"
            value={newImportAccount.email}
            onChange={(event) => setNewImportAccount((current) => ({ ...current, email: event.target.value }))}
            placeholder="neue Import-E-Mail, z. B. philip@test2.de"
            disabled={creatingImportAccount}
            required
          />
          <input
            style={styles.importCreateName}
            value={newImportAccount.displayName}
            onChange={(event) => setNewImportAccount((current) => ({ ...current, displayName: event.target.value }))}
            placeholder="Anzeigename (optional)"
            disabled={creatingImportAccount}
          />
          <button style={styles.importCreateBtn} type="submit" disabled={creatingImportAccount || !newImportAccount.userId || !newImportAccount.email.trim()}>
            {creatingImportAccount ? 'Wird angelegt…' : '+ Adresse hinzufügen'}
          </button>
          {importCreateError && <div style={styles.importCreateError}>{importCreateError}</div>}
        </form>

        <div style={styles.importGrid}>
          {importAccounts.map((account) => {
            const draft = importDrafts[String(account.id)] || {
              accountName: account.account_name || '',
              displayName: account.display_name || '',
              email: account.email || '',
            };
            const isSaving = savingImportAccount === account.id;
            return (
              <div key={account.id} style={styles.importRow}>
                <div>
                  <div style={{ fontSize: 12, color: '#eee', fontWeight: 700 }}>{account.user_email}</div>
                  <div style={{ fontSize: 10, color: '#777' }}>User #{account.user_id}</div>
                </div>
                <label style={styles.importField}>
                  <span style={styles.importFieldLabel}>Account-Name</span>
                  <input
                    value={draft.accountName}
                    disabled={isSaving}
                    onChange={(event) => updateImportDraft(account, 'accountName', event.target.value)}
                    placeholder="Account 1"
                    style={styles.importNameInput}
                  />
                </label>
                <label style={styles.importField}>
                  <span style={styles.importFieldLabel}>Anzeigename</span>
                  <input
                    value={draft.displayName}
                    disabled={isSaving}
                    onChange={(event) => updateImportDraft(account, 'displayName', event.target.value)}
                    placeholder="optional"
                    style={styles.importNameInput}
                  />
                </label>
                <label style={styles.importField}>
                  <span style={styles.importFieldLabel}>Import-E-Mail</span>
                  <input
                    type="email"
                    value={draft.email}
                    disabled={isSaving}
                    onChange={(event) => updateImportDraft(account, 'email', event.target.value)}
                    placeholder="ursprüngliche Amazon-E-Mail"
                    style={styles.importInput}
                  />
                </label>
                <div style={{ fontSize: 10, color: account.email ? '#4ade80' : '#fbbf24', whiteSpace: 'nowrap' }}>
                  {isSaving ? 'speichert…' : account.email ? '✓ aktiv' : '⚠ fehlt'}
                </div>
                <button
                  type="button"
                  style={{ ...styles.importSaveBtn, opacity: isSaving ? 0.6 : 1 }}
                  disabled={isSaving}
                  onClick={() => saveImportAccount(account)}
                >
                  {isSaving ? 'Speichert…' : 'Speichern'}
                </button>
              </div>
            );
          })}
          {importAccounts.length === 0 && <div style={{ color: '#777', fontSize: 12 }}>Noch keine Accounts vorhanden.</div>}
        </div>
      </div>

      <div style={styles.toolbar}>
        <input style={styles.search} placeholder="🔍 Email oder Operator-ID suchen..." value={search} onChange={(event) => setSearch(event.target.value)} />
        <button style={styles.refreshBtn} onClick={fetchData}>🔄 Aktualisieren</button>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {['ID', 'Email', 'Rolle', 'Operator', 'Daten-Eigentümer', 'Sichtbare Orte', 'Plan', 'Bestellungen', 'Wishlist', 'Registriert', 'Aktion'].map((heading) => (
                <th key={heading} style={styles.th}>{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((user) => (
              <tr key={user.id} style={styles.tr}>
                <td style={styles.td}><span style={styles.idBadge}>#{user.id}</span></td>
                <td style={styles.td}>
                  <div style={{ fontWeight: 500 }}>{user.email}</div>
                  {user.telegram_chat_id && <div style={{ fontSize: 11, color: '#888' }}>📱 {user.telegram_chat_id}</div>}
                </td>
                <td style={styles.td}><RoleBadge user={user} /></td>
                <td style={{ ...styles.td, fontFamily: 'monospace' }}>{user.delivery_operator_code || '—'}</td>
                <td style={styles.td}>{user.owner_email || '—'}</td>
                <td style={{ ...styles.td, minWidth: 250 }}>
                  {user.user_role === 'delivery_only' || user.plan === 'admin' ? (
                    <>
                      <CityCheckboxes
                        cities={cities}
                        selected={user.plan === 'admin' && (user.delivery_allowed_cities || []).length === 0
                          ? cities
                          : (user.delivery_allowed_cities || [])}
                        disabled={savingCities === user.id}
                        onToggle={(city, checked) => toggleUserCity(user, city, checked)}
                      />
                      {savingCities === user.id && <div style={styles.savingHint}>Speichert...</div>}
                      {user.plan === 'admin' && (user.delivery_allowed_cities || []).length === 0 && (
                        <div style={styles.savingHint}>Alle Orte sichtbar. Haken entfernen, um deinen Zugriff einzuschränken.</div>
                      )}
                      {user.user_role === 'delivery_only' && (user.delivery_allowed_cities || []).length === 0 && (
                        <div style={styles.warningHint}>Ohne Auswahl sieht der Benutzer keine Delivery-Daten.</div>
                      )}
                    </>
                  ) : '—'}
                </td>
                <td style={styles.td}>
                  <div style={styles.planRow}>
                    <PlanBadge plan={user.plan || 'free'} />
                    <select style={styles.planSelect} value={user.plan || 'free'} disabled={saving === user.id || user.user_role === 'delivery_only'} onChange={(event) => changePlan(user.id, event.target.value)}>
                      <option value="free">Free</option>
                      <option value="premium">Premium</option>
                      <option value="ultimate">Ultimate</option>
                      <option value="admin">Admin</option>
                      <option value="mirror">Mirror</option>
                    </select>
                    {saving === user.id && <span style={{ fontSize: 11, color: '#888' }}>⏳</span>}
                  </div>
                </td>
                <td style={{ ...styles.td, textAlign: 'center' }}>{user.order_count || 0}</td>
                <td style={{ ...styles.td, textAlign: 'center' }}>{user.wishlist_count || 0}</td>
                <td style={styles.td}><span style={{ fontSize: 12, color: '#888' }}>{new Date(user.created_at).toLocaleDateString('de-DE')}</span></td>
                <td style={styles.td}><button style={styles.deleteBtn} onClick={() => setConfirm({ userId: user.id, email: user.email })}>🗑️</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div style={styles.empty}>Keine User gefunden</div>}
      </div>

      {confirm && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={{ color: '#fff', marginBottom: 12 }}>User löschen?</h3>
            <p style={{ color: '#aaa', marginBottom: 20 }}><b style={{ color: '#e94560' }}>{confirm.email}</b> und alle zugehörigen Daten werden gelöscht.</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={styles.confirmDeleteBtn} onClick={() => deleteUser(confirm.userId)}>🗑️ Löschen</button>
              <button style={styles.cancelBtn} onClick={() => setConfirm(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ ...styles.statCard, borderColor: color }}>
      <div style={{ fontSize: 22, fontWeight: 'bold', color }}>{value}</div>
      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{label}</div>
    </div>
  );
}

const styles = {
  page: { padding: '24px', background: '#0f0f1a', minHeight: '100vh', color: '#eee', overflowX: 'auto' },
  title: { fontSize: 24, fontWeight: 'bold', marginBottom: 20, color: '#e94560' },
  loading: { textAlign: 'center', padding: 40, color: '#888' },
  error: { background: '#b71c1c22', border: '1px solid #b71c1c', borderRadius: 8, padding: 16, color: '#ffcdd2' },
  statsRow: { display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' },
  statCard: { background: '#1a1a2e', border: '1px solid', borderRadius: 8, padding: '12px 18px', minWidth: 100, textAlign: 'center' },
  cityManager: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', background: '#1a1a2e', border: '1px solid #334155', borderRadius: 10, padding: 16, marginBottom: 12 },
  createCard: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', background: '#1a1a2e', border: '1px solid #4c1d95', borderRadius: 10, padding: 16, marginBottom: 18 },
  createTitle: { color: '#ddd6fe', fontWeight: 800, marginBottom: 3 },
  createHint: { color: '#8f8da8', fontSize: 11 },
  formInput: { padding: '9px 10px', minWidth: 200, background: '#0d1b2a', color: '#eee', border: '1px solid #444', borderRadius: 6, fontSize: 12 },
  createBtn: { padding: '9px 14px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 },
  secondaryBtn: { padding: '9px 14px', background: '#334155', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 },
  createError: { width: '100%', color: '#fca5a5', fontSize: 12 },
  cityLabel: { color: '#c4b5fd', fontSize: 12, fontWeight: 700, marginBottom: 7 },
  cityGrid: { display: 'flex', gap: 7, flexWrap: 'wrap' },
  cityOption: { display: 'inline-flex', alignItems: 'center', gap: 5, background: '#0d1b2a', border: '1px solid #444', borderRadius: 6, padding: '5px 8px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' },
  savingHint: { color: '#93c5fd', fontSize: 10, marginTop: 5 },
  warningHint: { color: '#fbbf24', fontSize: 10, marginTop: 5 },
  importCard: { background: '#1a1a2e', border: '1px solid #334155', borderRadius: 10, padding: 16, marginBottom: 18 },
  importTitle: { color: '#c4b5fd', fontWeight: 800, marginBottom: 4 },
  importHint: { color: '#8f8da8', fontSize: 11, lineHeight: 1.5 },
  importCreateRow: { display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) minmax(260px, 2fr) minmax(160px, 1fr) auto', gap: 8, alignItems: 'center', margin: '14px 0 16px', padding: 10, background: '#111827', border: '1px solid #4c1d95', borderRadius: 8 },
  importCreateSelect: { width: '100%', padding: '8px 9px', background: '#0d1b2a', color: '#eee', border: '1px solid #444', borderRadius: 6, fontSize: 11 },
  importCreateInput: { width: '100%', padding: '8px 9px', background: '#0d1b2a', color: '#eee', border: '1px solid #444', borderRadius: 6, fontSize: 11, fontFamily: 'monospace' },
  importCreateName: { width: '100%', padding: '8px 9px', background: '#0d1b2a', color: '#eee', border: '1px solid #444', borderRadius: 6, fontSize: 11 },
  importCreateBtn: { padding: '8px 12px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap' },
  importCreateError: { gridColumn: '1 / -1', color: '#fca5a5', fontSize: 11 },
  importGrid: { display: 'flex', flexDirection: 'column', gap: 7 },
  importRow: { display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) minmax(130px, 180px) minmax(130px, 180px) minmax(240px, 2fr) 70px 90px', gap: 10, alignItems: 'end', padding: 9, background: '#0d1b2a', border: '1px solid #2b3445', borderRadius: 7 },
  importField: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  importFieldLabel: { color: '#777', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' },
  importNameInput: { width: '100%', padding: '7px 9px', background: '#111827', color: '#eee', border: '1px solid #444', borderRadius: 6, fontSize: 11 },
  importInput: { width: '100%', padding: '7px 9px', background: '#111827', color: '#eee', border: '1px solid #444', borderRadius: 6, fontSize: 11, fontFamily: 'monospace' },
  importSaveBtn: { padding: '7px 10px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  toolbar: { display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' },
  search: { flex: 1, padding: '8px 12px', background: '#1a1a2e', border: '1px solid #333', borderRadius: 6, color: '#eee', fontSize: 14 },
  refreshBtn: { padding: '8px 14px', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  tableWrap: { background: '#1a1a2e', borderRadius: 10, border: '1px solid #333', overflow: 'auto' },
  table: { width: '100%', minWidth: 1550, borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', background: '#0d1b2a', color: '#888', fontSize: 12, fontWeight: 600, borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #222' },
  td: { padding: '12px 16px', fontSize: 13, verticalAlign: 'middle' },
  idBadge: { background: '#333', color: '#aaa', padding: '2px 7px', borderRadius: 4, fontSize: 11 },
  planRow: { display: 'flex', alignItems: 'center', gap: 8 },
  planSelect: { background: '#0d1b2a', color: '#eee', border: '1px solid #444', borderRadius: 4, padding: '4px 6px', fontSize: 12, cursor: 'pointer' },
  deleteBtn: { background: 'none', border: '1px solid #444', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 14 },
  empty: { padding: 24, textAlign: 'center', color: '#555' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#1a1a2e', border: '1px solid #444', borderRadius: 12, padding: 24, minWidth: 340 },
  confirmDeleteBtn: { flex: 1, padding: '10px', background: '#b71c1c', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold' },
  cancelBtn: { flex: 1, padding: '10px', background: '#333', color: '#eee', border: 'none', borderRadius: 6, cursor: 'pointer' },
};
